import { Lightbulb, Sparkles, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatMoney } from "../../lib/currency";
import { Row } from "./atoms";
import MachineRadarChart from "./MachineRadarChart";

export default function CostsTab({ q, c: _c, t, a }) {
  const { t: tr } = useTranslation();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="costs-tab">
      <div className="tech-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-700/60 label-tech">{tr("quoteTabs.costsSummary")}</div>
        <div className="divide-y divide-slate-800">
          <Row label={tr("quoteTabs.machineCost")} value={formatMoney(t.machine_cost_eur, q.currency)}/>
          <Row label={tr("quoteTabs.materialCost")} value={formatMoney(t.material_cost_eur, q.currency)}/>
          <Row label={tr("quoteTabs.subtotal")} value={formatMoney(t.subtotal_eur, q.currency)} bold/>
          <Row label={tr("quoteTabs.commercialMargin", { pct: t.margin_pct })} value={formatMoney(t.margin_eur, q.currency)}/>
          <Row label={tr("quoteTabs.batchTotal")} value={formatMoney(t.total_eur, q.currency)} accent/>
          <Row label={tr("quoteTabs.unitPriceOn", { count: t.batch_size })} value={formatMoney(t.unit_price_eur, q.currency)} accent/>
        </div>
      </div>

      <div className="space-y-5">
        {q.recommendations?.length > 0 && q.recommendations.some((r) => r.spindle_kw != null || r.max_size_mm != null) && (
          <MachineRadarChart machines={q.recommendations}/>
        )}

        {q.recommendations?.length > 0 && (
          <div className="tech-card p-5" data-testid="recommendations">
            <div className="flex items-center gap-2 mb-3 text-blue-400">
              <Lightbulb size={16}/> <div className="label-tech text-blue-400">{tr("quoteTabs.bestMachines")}</div>
            </div>
            <div className="space-y-3">
              {q.recommendations.map((r, i) => (
                <div key={`rec-${r.machine_id}-${i}`} className={`p-3 rounded-md border ${r.owned ? "border-emerald-500/40 bg-emerald-500/5" : "border-slate-700 bg-slate-900/40"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-slate-100 text-sm">{r.machine_name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{r.category}{r.axes ? ` · ${r.axes}` : ""}</div>
                    </div>
                    {r.owned ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0"/> : <Sparkles size={14} className="text-blue-400 shrink-0"/>}
                  </div>

                  {/* Coverage + commercial */}
                  <div className="mt-2 text-xs grid grid-cols-3 gap-2">
                    <div><div className="text-slate-500">{tr("quoteTabs.coverageShort")}</div><div className="font-mono-data text-slate-100">{r.operations_covered_count}/{r.total_operations}</div></div>
                    <div><div className="text-slate-500">€/h</div><div className="font-mono-data text-slate-100">{r.hourly_rate}</div></div>
                    <div><div className="text-slate-500">{tr("quoteTabs.precisionShort")}</div><div className="font-mono-data text-slate-100">±{r.precision_um}µm</div></div>
                  </div>

                  {/* Tech specs row — appears only when at least one is present */}
                  {(r.spindle_kw != null || r.max_torque_nm != null || r.max_rpm != null || r.roughing_capacity_cm3_min != null) && (
                    <div className="mt-2 pt-2 border-t border-slate-800 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                      {r.spindle_kw != null && (
                        <div title={tr("quoteTabs.spindlePower")} data-testid={`rec-spindle-${r.machine_id}`}>
                          <div className="text-slate-500">{tr("quoteTabs.spindle")}</div>
                          <div className="font-mono-data text-cyan-300">{r.spindle_kw} kW</div>
                        </div>
                      )}
                      {r.max_torque_nm != null && r.max_torque_nm > 0 && (
                        <div title={tr("quoteTabs.maxTorque")}>
                          <div className="text-slate-500">{tr("quoteTabs.torque")}</div>
                          <div className="font-mono-data text-cyan-300">{r.max_torque_nm} Nm</div>
                        </div>
                      )}
                      {r.max_rpm != null && r.max_rpm > 0 && (
                        <div title={tr("quoteTabs.maxRpm")}>
                          <div className="text-slate-500">{tr("quoteTabs.rpmShort")}</div>
                          <div className="font-mono-data text-cyan-300">{r.max_rpm.toLocaleString("it-IT")}</div>
                        </div>
                      )}
                      {r.roughing_capacity_cm3_min != null && (
                        <div title={tr("quoteTabs.roughingCapacity")}>
                          <div className="text-slate-500">{tr("quoteTabs.roughing")}</div>
                          <div className="font-mono-data text-cyan-300">{r.roughing_capacity_cm3_min} cm³/min</div>
                        </div>
                      )}
                      {r.press_force_kn != null && (
                        <div title={tr("quoteTabs.pressForce")}>
                          <div className="text-slate-500">{tr("quoteTabs.force")}</div>
                          <div className="font-mono-data text-cyan-300">{r.press_force_kn} kN</div>
                        </div>
                      )}
                      {r.laser_power_kw != null && (
                        <div title={tr("quoteTabs.laserPower")}>
                          <div className="text-slate-500">{tr("quoteTabs.laser")}</div>
                          <div className="font-mono-data text-cyan-300">{r.laser_power_kw} kW</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Envelope / capacity badge */}
                  {r.max_size_mm != null && (
                    <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-emerald-500/10 border border-emerald-500/30 text-[10px] font-mono-data text-emerald-300" title={tr("quoteTabs.maxPartSize")}>
                      ✓ {tr("quoteTabs.capacity")}: {r.max_size_mm} mm
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {a.alternative_methods?.length > 0 && (
          <div className="tech-card p-5" data-testid="alt-methods">
            <div className="flex items-center gap-2 mb-3 text-blue-400">
              <Sparkles size={16}/> <div className="label-tech text-blue-400">{tr("quoteTabs.altMethods")}</div>
            </div>
            <ul className="space-y-2 text-sm text-slate-300">
              {a.alternative_methods.map((m, i) => <li key={`alt-${i}`} className="flex gap-2"><span className="text-blue-400">→</span> {m}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
